import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useCallback, useEffect } from "react";
import PortalContainer from './PortalContainer';
import classNames from 'classnames';
/**
 * @export
 * A component that displays a tooltip when hovering over its child element.
 * Uses v5 styling patterns and positioning logic.
 *
 * @example Basic tooltip
 * ```tsx
 * <Tooltip content="Helpful hint">
 *   <button>Hover me</button>
 * </Tooltip>
 * ```
 *
 * @example Custom position
 * ```tsx
 * <Tooltip content="I appear on the right" position="right">
 *   <span>Hover here</span>
 * </Tooltip>
 * ```
 *
 * @example JSX content
 * ```tsx
 * <Tooltip content={() => <strong>Bold tooltip</strong>}>
 *   <button>Fancy tooltip</button>
 * </Tooltip>
 * ```
 */
const Tooltip = ({ content, position = 'top', showArrow = true, children }) => {
    const [show, setShow] = useState(false);
    const [coords, setCoords] = useState({});
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const updatePosition = useCallback(() => {
        if (!triggerRef.current)
            return;
        const rect = triggerRef.current.getBoundingClientRect();
        const { innerHeight, innerWidth } = window;
        const gap = 8;
        let newCoords = {};
        switch (position) {
            case "top":
                newCoords = {
                    left: rect.left + rect.width / 2,
                    bottom: innerHeight - rect.top + gap,
                    transform: 'translateX(-50%)',
                };
                break;
            case "bottom":
                newCoords = {
                    left: rect.left + rect.width / 2,
                    top: rect.bottom + gap,
                    transform: 'translateX(-50%)',
                };
                break;
            case "right":
                newCoords = {
                    left: rect.right + gap,
                    top: rect.top + rect.height / 2,
                    transform: 'translateY(-50%)',
                };
                break;
            case "left":
                newCoords = {
                    right: innerWidth - rect.left + gap,
                    top: rect.top + rect.height / 2,
                    transform: 'translateY(-50%)',
                };
                break;
        }
        setCoords(newCoords);
    }, [position]);
    useEffect(() => {
        if (show) {
            requestAnimationFrame(() => {
                updatePosition();
            });
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
            return () => {
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition, true);
            };
        }
    }, [show, updatePosition]);
    const handleMouseEnter = useCallback(() => {
        setShow(true);
    }, []);
    const handleMouseLeave = useCallback(() => {
        setShow(false);
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx("span", { className: "uxpcore_tooltip__container", onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, ref: triggerRef, children: children }), show && (_jsx(PortalContainer, { children: _jsx("div", { className: classNames("uxpcore_tooltip__content", `uxpcore_tooltip__content--${position}`, { 'uxpcore_tooltip__content--arrow': showArrow }), style: coords, ref: tooltipRef, children: typeof content === "string" ? content : content() }) }))] }));
};
export default Tooltip;
